import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 60);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  const handleCategoryClick = (slug: string) => {
    navigate(`/category/${slug}`);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/70'
          : 'bg-transparent'
      }`}
    >
      <div className="w-full px-4 md:px-6">
        <div className="flex items-center justify-between h-16">
          <Link
            to="/"
            className="flex items-center gap-2 group"
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          >
            <div className="w-9 h-9 flex items-center justify-center">
              <i className="ri-play-circle-fill text-3xl text-primary-500"></i>
            </div>
            <span className="text-xl font-heading font-bold text-foreground-900 whitespace-nowrap">
              桃子视频
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-1">
            <Link
              to="/"
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                location.pathname === '/'
                  ? 'text-primary-600 bg-primary-50'
                  : 'text-foreground-700 hover:text-foreground-900 hover:bg-background-100'
              }`}
            >
              首页
            </Link>

            <div className="relative group">
              <button className="px-4 py-2 rounded-lg text-sm font-medium text-foreground-700 hover:text-foreground-900 hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer flex items-center gap-1">
                分类浏览
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-down-s-line text-sm"></i>
                </div>
              </button>
              <div className="absolute top-full left-0 mt-1 w-56 bg-background-50 border border-background-200/70 rounded-xl shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 py-2">
                {categories.map((cat) => (
                  <button
                    key={cat.slug}
                    onClick={() => handleCategoryClick(cat.slug)}
                    className="w-full text-left px-4 py-2.5 text-sm text-foreground-700 hover:text-foreground-900 hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer flex items-center gap-3"
                  >
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className={`${cat.icon} text-base text-primary-500`}></i>
                    </div>
                    {cat.name}
                  </button>
                ))}
              </div>
            </div>

            <Link
              to="/about"
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                location.pathname === '/about'
                  ? 'text-primary-600 bg-primary-50'
                  : 'text-foreground-700 hover:text-foreground-900 hover:bg-background-100'
              }`}
            >
              关于我们
            </Link>
          </div>

          <div className="hidden md:flex items-center gap-2">
            <div className="relative">
              <div className="w-5 h-5 flex items-center justify-center absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none">
                <i className="ri-search-line text-sm text-foreground-400"></i>
              </div>
              <input
                type="text"
                placeholder="搜索视频..."
                className="pl-9 pr-4 py-2 text-sm rounded-full bg-background-100 border border-background-200/70 text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 w-48 transition-all"
              />
            </div>
          </div>

          <button
            className="md:hidden w-10 h-10 flex items-center justify-center rounded-lg text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            <div className="w-5 h-5 flex items-center justify-center">
              <i className={`text-xl ${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
            </div>
          </button>
        </div>

        {isMobileMenuOpen && (
          <div className="md:hidden border-t border-background-200/70 py-4 space-y-1">
            <Link
              to="/"
              className={`block px-4 py-3 rounded-lg text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                location.pathname === '/'
                  ? 'text-primary-600 bg-primary-50'
                  : 'text-foreground-700 hover:bg-background-100'
              }`}
            >
              首页
            </Link>
            <div className="px-4 py-2 text-xs font-medium text-foreground-400 uppercase tracking-wider">
              分类浏览
            </div>
            {categories.map((cat) => (
              <button
                key={cat.slug}
                onClick={() => handleCategoryClick(cat.slug)}
                className="w-full text-left px-4 py-3 rounded-lg text-sm text-foreground-700 hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer flex items-center gap-3"
              >
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className={`${cat.icon} text-base text-primary-500`}></i>
                </div>
                {cat.name}
              </button>
            ))}
            <Link
              to="/about"
              className={`block px-4 py-3 rounded-lg text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                location.pathname === '/about'
                  ? 'text-primary-600 bg-primary-50'
                  : 'text-foreground-700 hover:bg-background-100'
              }`}
            >
              关于我们
            </Link>
            <div className="px-4 pt-2">
              <div className="relative">
                <div className="w-5 h-5 flex items-center justify-center absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none">
                  <i className="ri-search-line text-sm text-foreground-400"></i>
                </div>
                <input
                  type="text"
                  placeholder="搜索视频..."
                  className="w-full pl-9 pr-4 py-2.5 text-sm rounded-full bg-background-100 border border-background-200/70 text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300"
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}